import { CheckCircleFilled, CloseCircleOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Card, Form, message, Space, Typography } from "antd";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { propertyService } from "../../../../../configs/constants";
import { useGetOrganization } from "../../../../../api/v1";
import { buildUploadPath, hasHeroGalleryImage } from "../../../../organism/ImagesAndQrCode/utils";
import { TemplateField } from "../../../../../types/template";
import MarketingAttributes from "../MarketingAttributes";
import { buildMarketingDataPayload, flattenMarketingDataForForm } from "./marketingDataPayload";
import { createMarketingSlug, stripMarketingUrlSchemeForDisplay } from "./marketingUrlUtils";

const { Title } = Typography;
const propertyMarketingFields = require("./marketingFields.json") as TemplateField[];
const unitMarketingFields = require("./unitMarketingFields.json") as TemplateField[];

const RESIDENTIAL_ONLY_FIELDS = new Set(["beds", "baths"]);

const PUBLISH_REQUIRES_HERO_MESSAGE =
  "Add and save a hero image (first image in Images & QR Code) before publishing.";

type MarketingFormValues = {
  dynamicFields?: Record<string, unknown>;
};

type MarketingProps = {
  marketingPublicListUrl?: string;
  entity?: "property" | "unit";
  usageType?: string;
  unitName?: string;
  /** Optional parent property slug for unit paths: `{propertySlug}/{unitSlug}` */
  propertySlug?: string;
  isActive?: boolean;
  propertyDetails: {
    propertyId?: string | number;
    unitId?: string | number;
    _id?: string | number;
    buildingName?: string;
    propertyName?: string;
    marketingData?: Record<string, unknown>;
    customFields?: Record<string, unknown>;
    galleryImages?: { position?: string | number; documentUrl?: string; documentId?: string }[];
    unit?: {
      unitCode?: string;
      usageType?: string;
      usagesType?: string;
    };
  };
  onSaveSuccess?: () => void | Promise<void>;
  /** Optional storage path segments for media uploads: org/property[/unit]. */
  uploadPathSegments?: {
    propertyName?: string;
    unitCode?: string;
  };
};

const getSavedMarketingValues = (propertyDetails: MarketingProps["propertyDetails"]) => {
  if (propertyDetails.marketingData && Object.keys(propertyDetails.marketingData).length > 0) {
    return flattenMarketingDataForForm(propertyDetails.marketingData);
  }
  // No marketingData → leave amenities to org defaults (all selected).
  return undefined;
};

const hasSavedFieldValue = (savedValues: Record<string, unknown> | undefined, fieldName: string) => {
  if (!savedValues || !Object.prototype.hasOwnProperty.call(savedValues, fieldName)) return false;
  const value = savedValues[fieldName];
  if (value == null) return false;
  return String(value).trim() !== "";
};

const Marketing = ({
  propertyDetails,
  onSaveSuccess,
  marketingPublicListUrl,
  entity = "property",
  usageType,
  unitName,
  propertySlug,
  isActive = true,
  uploadPathSegments,
}: MarketingProps) => {
  const { organization } = useGetOrganization({ staleTimeInfinity: true });
  const form = useForm<MarketingFormValues>();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
    setValue,
  } = form;
  const [noEdit, setNoEdit] = useState(true);

  const resolvedUsageType = usageType || propertyDetails.unit?.usageType || propertyDetails.unit?.usagesType;
  const isResidential = String(resolvedUsageType || "").toLowerCase() === "residential";
  const listingTitleValue = watch("dynamicFields.listingTitle");
  const publishedValue = watch("dynamicFields.published");

  const resolveUnitSlug = (titleOrCode: string) => {
    // Unit slug path: `{property-slug}/{unit-slug}` — listing title stays unit code only.
    const unitPart = createMarketingSlug(titleOrCode || propertyDetails.unit?.unitCode || "");
    const rawParent = String(
      propertySlug || propertyDetails.propertyName || propertyDetails.buildingName || "",
    ).trim();
    // Keep an existing path segment; otherwise slugify the property name/slug.
    const parentSlug = rawParent.includes("/")
      ? rawParent.replace(/^\/+|\/+$/g, "")
      : createMarketingSlug(rawParent);

    if (parentSlug && unitPart) return `${parentSlug}/${unitPart}`;
    return unitPart;
  };

  const isCompleteUnitSlug = (slug: unknown) => {
    const value = String(slug || "").trim();
    if (!value || !value.includes("/")) return false;
    const [parentPart, ...rest] = value.split("/");
    return Boolean(parentPart && rest.join("/"));
  };

  const savedMarketingValues = useMemo(
    () => getSavedMarketingValues(propertyDetails),
    [propertyDetails.marketingData, propertyDetails.customFields],
  );

  const heroImageAvailable = useMemo(
    () => hasHeroGalleryImage(propertyDetails.galleryImages),
    [propertyDetails.galleryImages],
  );

  const isPublishedSaved = Boolean(savedMarketingValues?.published);

  const applyPublishedFieldRules = (field: TemplateField): TemplateField => {
    if (field.name !== "published") {
      return field;
    }

    if (heroImageAvailable || isPublishedSaved) {
      return field;
    }

    return {
      ...field,
      isDisabled: true,
      helperText: PUBLISH_REQUIRES_HERO_MESSAGE,
    };
  };

  const templateFields = useMemo(() => {
    const customFields = savedMarketingValues;

    if (entity === "unit") {
      const nameSource = propertyDetails.unit?.unitCode || unitName || "";
      const titleDefault = hasSavedFieldValue(customFields, "listingTitle") ? undefined : nameSource;
      const slugSource = hasSavedFieldValue(customFields, "listingTitle")
        ? String(customFields?.listingTitle)
        : nameSource;
      // Rebuild when missing or incomplete (unit-code only from older saves)
      const slugDefault =
        !hasSavedFieldValue(customFields, "slug") || !isCompleteUnitSlug(customFields?.slug)
          ? resolveUnitSlug(slugSource)
          : undefined;
      const qrDefault = hasSavedFieldValue(customFields, "qrCodeUrl")
        ? undefined
        : stripMarketingUrlSchemeForDisplay(marketingPublicListUrl || "");

      return unitMarketingFields
        .filter((field) => isResidential || !RESIDENTIAL_ONLY_FIELDS.has(field.name))
        .map((field) => {
          if (field.name === "listingTitle" && titleDefault) {
            return { ...field, default: titleDefault };
          }
          if (field.name === "slug" && slugDefault) {
            return { ...field, default: slugDefault };
          }
          if (field.name === "qrCodeUrl" && qrDefault) {
            return { ...field, default: qrDefault };
          }
          return applyPublishedFieldRules(field);
        });
    }

    const propertyName = propertyDetails.buildingName || "";
    const titleDefault = hasSavedFieldValue(customFields, "listingTitle") ? undefined : propertyName;
    const slugSource = hasSavedFieldValue(customFields, "listingTitle")
      ? String(customFields?.listingTitle)
      : propertyName;
    const slugDefault = hasSavedFieldValue(customFields, "slug") ? undefined : createMarketingSlug(slugSource);
    const qrDefault = hasSavedFieldValue(customFields, "qrCodeUrl")
      ? undefined
      : stripMarketingUrlSchemeForDisplay(marketingPublicListUrl || "");

    return propertyMarketingFields.map((field) => {
      if (field.name === "listingTitle" && titleDefault) {
        return { ...field, default: titleDefault };
      }
      if (field.name === "slug" && slugDefault) {
        return { ...field, default: slugDefault };
      }
      if (field.name === "qrCodeUrl" && qrDefault) {
        return { ...field, default: qrDefault };
      }
      return applyPublishedFieldRules(field);
    });
  }, [
    entity,
    heroImageAvailable,
    isPublishedSaved,
    isResidential,
    marketingPublicListUrl,
    propertyDetails.buildingName,
    propertyDetails.propertyName,
    propertyDetails.unit?.unitCode,
    propertySlug,
    savedMarketingValues,
    unitName,
  ]);

  useEffect(() => {
    if (entity !== "unit") {
      if (Boolean(publishedValue) || hasSavedFieldValue(savedMarketingValues, "slug")) return;

      const titleSource = String(listingTitleValue || "").trim() || propertyDetails.buildingName || "";
      setValue("dynamicFields.slug", createMarketingSlug(titleSource), { shouldDirty: false });
      return;
    }

    // Unit: always keep `property-slug/unit-slug` while unpublished (even if older save was unit-only).
    if (Boolean(publishedValue) && isCompleteUnitSlug(savedMarketingValues?.slug)) return;

    const titleSource =
      String(listingTitleValue || "").trim() || propertyDetails.unit?.unitCode || unitName || "";
    const nextSlug = resolveUnitSlug(titleSource);
    if (!nextSlug.includes("/")) return; // wait until property slug/name is available

    setValue("dynamicFields.slug", nextSlug, { shouldDirty: false });
  }, [
    entity,
    listingTitleValue,
    publishedValue,
    propertyDetails.buildingName,
    propertyDetails.propertyName,
    propertyDetails.unit?.unitCode,
    propertySlug,
    savedMarketingValues,
    setValue,
    unitName,
  ]);

  useEffect(() => {
    if (Boolean(publishedValue) || hasSavedFieldValue(savedMarketingValues, "qrCodeUrl")) return;
    if (!marketingPublicListUrl) return;

    setValue("dynamicFields.qrCodeUrl", stripMarketingUrlSchemeForDisplay(marketingPublicListUrl), {
      shouldDirty: false,
    });
  }, [marketingPublicListUrl, publishedValue, savedMarketingValues, setValue]);

  const onSubmit = async (values: MarketingFormValues) => {
    if (!values.dynamicFields) return;

    if (values.dynamicFields.published && !heroImageAvailable) {
      message.error(PUBLISH_REQUIRES_HERO_MESSAGE);
      return;
    }

    const assetId =
      entity === "unit"
        ? propertyDetails.unitId ?? propertyDetails._id
        : propertyDetails.propertyId ?? propertyDetails._id;

    if (!assetId) {
      message.error("Unable to update marketing information");
      return;
    }

    try {
      const mediaPathPrefix =
        entity === "unit" ? buildUploadPath(organization?.name, uploadPathSegments) || undefined : undefined;
      const { marketingData } = buildMarketingDataPayload(values.dynamicFields, { mediaPathPrefix });
      const existingMarketingData =
        propertyDetails.marketingData && typeof propertyDetails.marketingData === "object"
          ? propertyDetails.marketingData
          : {};

      // Keep property/unit-info fields that live in marketingData but are edited outside Marketing tab.
      if (entity === "unit") {
        if ("parkingSpaces" in existingMarketingData) {
          marketingData.parkingSpaces = existingMarketingData.parkingSpaces;
        }
        if ("furnished" in existingMarketingData) {
          marketingData.furnished = existingMarketingData.furnished;
        }
      }

      if (entity === "property") {
        if ("numberOfFloors" in existingMarketingData) {
          marketingData.numberOfFloors = existingMarketingData.numberOfFloors;
        }
        if ("parkingTypes" in existingMarketingData) {
          marketingData.parkingTypes = existingMarketingData.parkingTypes;
        } else if ("parkingPlaces" in existingMarketingData) {
          marketingData.parkingTypes = existingMarketingData.parkingPlaces;
        }
      }

      await axios.patch(`${propertyService}/${entity}/${assetId}`, { marketingData });
      message.success("Marketing information updated");
      setNoEdit(true);
      await onSaveSuccess?.();
    } catch (error) {
      const responseError = error as { response?: { data?: { error?: string } } };
      message.error(responseError.response?.data?.error || "Unable to update marketing information");
    }
  };

  const handleCancel = () => {
    reset();
    setNoEdit(true);
  };

  return (
    <FormProvider {...form}>
      <Form layout="vertical" className="bf-main" onFinish={handleSubmit(onSubmit)}>
        <Card
          bordered={false}
          bodyStyle={{ padding: "20px 32px 24px" }}
          headStyle={{ padding: "16px 32px 0", borderBottom: 0 }}
          title={
            <Title level={5} className="ag-primary mb-0">
              Marketing Information
            </Title>
          }
          extra={
            <Space>
              {noEdit ? (
                <Button type="link" icon={<EditOutlined />} onClick={() => setNoEdit(false)}>
                  Edit
                </Button>
              ) : (
                <>
                  <Button type="link" icon={<CloseCircleOutlined />} onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button type="link" htmlType="submit" icon={<CheckCircleFilled />} loading={isSubmitting}>
                    Save
                  </Button>
                </>
              )}
            </Space>
          }
        >
          <MarketingAttributes
            control={control}
            errors={errors}
            setValue={setValue}
            entity={entity}
            noEdit={noEdit}
            savedValues={savedMarketingValues}
            marketingPublicListUrl={marketingPublicListUrl}
            templateFields={templateFields}
            isActive={isActive}
            uploadPathSegments={uploadPathSegments}
          />
        </Card>
      </Form>
    </FormProvider>
  );
};

export default Marketing;
